import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, Loader2 } from "lucide-react";
import { API } from "../config/api";

const fmtMoney = (n) => {
  if (n == null || isNaN(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
};

/**
 * "Top scorers who bought this" — small companion widget that surfaces the
 * highest-leaderboard-scoring politicians who have recently bought the
 * currently-viewed ticker. Renders right under the existing "Politicians (PTR)"
 * area on the Stock Analysis page so it pulls double duty as social-proof
 * and an actionable per-ticker signal.
 *
 * Data: GET /api/political-trades/top-buyers/{ticker}?days=180&limit=3
 */
export default function PoliticianBuyersWidget({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticker) return;
    let canc = false;
    let pollId = null;
    setLoading(true);
    const fetchOnce = async () => {
      try {
        const r = await axios.get(
          `${API}/political-trades/top-buyers/${ticker}?days=180&limit=3`,
          { timeout: 20000 }
        );
        if (!canc) {
          setData(r.data);
          // If the backend is still warming the leaderboard cache, poll until ready
          if (!r.data?.ready && r.data?.computing) {
            if (!pollId) {
              pollId = setInterval(fetchOnce, 8000);
            }
          } else if (pollId) {
            clearInterval(pollId);
            pollId = null;
          }
        }
      } catch {
        if (!canc) setData(null);
      } finally {
        if (!canc) setLoading(false);
      }
    };
    fetchOnce();
    return () => {
      canc = true;
      if (pollId) clearInterval(pollId);
    };
  }, [ticker]);

  return (
    <Card
      className="bg-gradient-to-br from-slate-900/80 to-slate-950/80 border-slate-800"
      data-testid="politician-buyers-widget"
    >
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-white text-sm flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-400" />
          Top scorers who bought {ticker}
        </CardTitle>
        <a
          href="/political-trades"
          className="text-[10px] text-emerald-400 hover:text-emerald-300 hover:underline"
        >
          full leaderboard →
        </a>
      </CardHeader>
      <CardContent className="text-xs">
        {loading ? (
          <div className="flex items-center text-slate-500">
            <Loader2 className="w-3 h-3 animate-spin mr-2" /> Scoring…
          </div>
        ) : !data?.rows?.length ? (
          <div className="text-slate-500 text-[11px]">
            {data?.note ||
              `No leaderboard-scoring politicians have bought ${ticker} in the last 180 days.`}
          </div>
        ) : (
          <div className="space-y-2">
            {data.rows.map((r, i) => (
              <div
                key={r.filer}
                className="flex items-center justify-between gap-2 p-2 rounded bg-slate-800/40 hover:bg-slate-800/70 transition"
                data-testid={`politician-buyer-${i}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-amber-400 font-bold text-[11px]">#{i + 1}</span>
                    <span className="text-white font-semibold truncate">{r.filer}</span>
                    <span
                      className={`text-[9px] font-semibold px-1 py-0.5 rounded uppercase ${
                        r.chamber === "senate"
                          ? "bg-purple-900/50 text-purple-300"
                          : "bg-blue-900/50 text-blue-300"
                      }`}
                    >
                      {r.chamber}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {r.state} · {r.n_buys_this_ticker} buy{r.n_buys_this_ticker > 1 ? "s" : ""} of {ticker} ·{" "}
                    {fmtMoney(r.buy_mid_this_ticker)} · last {r.last_tx_this_ticker}
                  </div>
                </div>
                <div
                  className={`text-right font-bold tabular-nums whitespace-nowrap ${
                    r.weighted_pct_change >= 0 ? "text-emerald-300" : "text-red-300"
                  }`}
                  title={`Overall score from ${r.n_buys_total} disclosed buys`}
                >
                  {r.weighted_pct_change >= 0 ? "+" : ""}
                  {r.weighted_pct_change.toFixed(1)}%
                  <div className="text-[9px] text-slate-500 font-normal">overall score</div>
                </div>
              </div>
            ))}
            <div className="text-[10px] text-slate-600 text-center pt-1">
              Score = midpoint-$-weighted % gain on each filer's recent disclosed buys.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
